/**
 * Framework-agnostic roll-up logic for the LLM usage statistics view.
 *
 * The backend returns a flat list of raw daily rows (`UsageCostRow[]`) with org,
 * user, model and purpose data embedded on every row. All aggregation —
 * pies/sunburst breakdowns and over-time series — happens here so the Vue
 * component stays thin and this stays unit-testable.
 */
import type { UsageCostRow } from '@/api';
import type { StackedSeries } from '@/components/admin/StatisticsStackedBarChartCard.vue';
import { formatStatisticsKeyLabel } from '@/utils/adminStatistics';
import {
  categoricalColor,
  childHueSpread,
  colorFromHue,
  lightnessForDepth,
  seededHue,
  shadeHueForTokenType,
  spreadHue,
  type TokenType,
} from '@/utils/usageColors';

export type Metric = 'cost' | 'tokens' | 'calls';

export type Dimension =
  'platform' | 'org' | 'user' | 'provider' | 'model' | 'purpose' | 'purpose_detail';

const UNKNOWN_LABEL = 'Unknown';
/** Separator for building unique sunburst node ids from a path of segments. */
const ID_SEP = ' › ';
const TOKEN_TYPES: TokenType[] = ['input', 'output', 'cached'];
const TOKEN_TYPE_LABEL: Record<TokenType, string> = {
  input: 'Input',
  output: 'Output',
  cached: 'Cached Input',
};

/** Safe string|number|null → number. */
export function toNumber(value: string | number | null | undefined): number {
  if (value === null || value === undefined) return 0;
  const num = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(num) ? num : 0;
}

/** Total metric value carried by a single row. */
export function rowMetric(row: UsageCostRow, metric: Metric): number {
  switch (metric) {
    case 'cost':
      return toNumber(row.input_cost) + toNumber(row.output_cost) + toNumber(row.cached_input_cost);
    case 'tokens':
      return row.input_tokens + row.output_tokens + row.cached_input_tokens;
    case 'calls':
      return row.call_count;
  }
}

/** Metric value for one token type. `calls` has no token-type split. */
export function rowMetricByTokenType(
  row: UsageCostRow,
  metric: Metric,
  tokenType: TokenType
): number {
  if (metric === 'cost') {
    const field = (
      { input: 'input_cost', output: 'output_cost', cached: 'cached_input_cost' } as const
    )[tokenType];
    return toNumber(row[field]);
  }
  if (metric === 'tokens') {
    const field = (
      { input: 'input_tokens', output: 'output_tokens', cached: 'cached_input_tokens' } as const
    )[tokenType];
    return row[field];
  }
  // calls: no split — attribute everything to the "input" slot, nothing elsewhere.
  return tokenType === 'input' ? row.call_count : 0;
}

/** Human-readable label for a row's value of the given dimension. */
export function dimensionLabel(row: UsageCostRow, dim: Dimension): string {
  switch (dim) {
    case 'platform':
      return formatStatisticsKeyLabel(row.platform);
    case 'org':
      return row.org_name ?? row.platform_org_uid ?? UNKNOWN_LABEL;
    case 'user':
      return row.user_full_name ?? row.user_email ?? row.platform_user_uid ?? UNKNOWN_LABEL;
    case 'provider':
      return formatStatisticsKeyLabel(row.provider);
    case 'model':
      return row.model || UNKNOWN_LABEL;
    case 'purpose':
      return row.purpose ? formatStatisticsKeyLabel(row.purpose) : UNKNOWN_LABEL;
    case 'purpose_detail':
      return row.purpose_detail ? formatStatisticsKeyLabel(row.purpose_detail) : UNKNOWN_LABEL;
  }
}

/** Stable colour seed for a row's value of the given dimension. */
export function dimensionSeed(row: UsageCostRow, dim: Dimension): string {
  switch (dim) {
    case 'org':
      return row.platform_org_uid ?? row.org_name ?? UNKNOWN_LABEL;
    case 'user':
      return row.platform_user_uid ?? row.user_email ?? UNKNOWN_LABEL;
    default:
      return dimensionLabel(row, dim);
  }
}

/** Aggregate rows by a single dimension, sorted by value descending. */
export function aggregateByDimension(
  rows: UsageCostRow[],
  dim: Dimension,
  metric: Metric
): [string, number][] {
  const totals = new Map<string, number>();
  for (const row of rows) {
    const label = dimensionLabel(row, dim);
    totals.set(label, (totals.get(label) ?? 0) + rowMetric(row, metric));
  }
  return Array.from(totals.entries()).sort((a, b) => b[1] - a[1]);
}

/**
 * Build per-day stacked series, one series per value of `dim`.
 * Shape matches `StatisticsStackedBarChartCard`'s expectations.
 */
export function buildTimeSeries(
  rows: UsageCostRow[],
  dim: Dimension,
  metric: Metric
): { days: string[]; series: StackedSeries[] } {
  const days = Array.from(new Set(rows.map(row => row.bucket_day))).sort();
  const dayIndex = new Map(days.map((day, index) => [day, index]));

  // label -> { values aligned to days, total for sorting }
  const groups = new Map<string, { values: number[]; total: number }>();
  for (const row of rows) {
    const label = dimensionLabel(row, dim);
    let group = groups.get(label);
    if (!group) {
      group = { values: Array.from({ length: days.length }, () => 0), total: 0 };
      groups.set(label, group);
    }
    const dayIdx = dayIndex.get(row.bucket_day);
    if (dayIdx === undefined) continue;
    const value = rowMetric(row, metric);
    group.values[dayIdx] += value;
    group.total += value;
  }

  // Colour by sorted position (golden-angle), so adjacent series stay far apart
  // on the hue wheel regardless of how similar the underlying labels/uids are.
  const series: StackedSeries[] = Array.from(groups.entries())
    .sort((a, b) => b[1].total - a[1].total)
    .map(([name, group], index) => ({
      name,
      values: group.values,
      color: categoricalColor(index),
    }));

  return { days, series };
}

export interface SunburstPath {
  dimensions: Dimension[];
  /** Add an innermost input/output/cached ring (ignored for the `calls` metric). */
  tokenTypeLeaf?: boolean;
}

export interface SunburstData {
  ids: string[];
  labels: string[];
  parents: string[];
  values: number[];
  colors: string[];
}

interface SunburstNode {
  id: string;
  label: string;
  parent: string;
  value: number;
  depth: number;
  /** Colour seed (uid) for root nodes. */
  seed: string;
  /** Set on the optional innermost input/output/cached leaves. */
  tokenType?: TokenType;
  /** Hue/colour are assigned in a second top-down pass once the tree is known. */
  hue: number;
  color: string;
}

/**
 * Build Plotly sunburst arrays from rows along a dimension path.
 *
 * Every node carries the total of its subtree (`branchvalues: 'total'`).
 *
 * Colour flows down the tree in a second pass: each root ring gets a stable hue
 * seeded from its uid, and a node's children are spread *evenly* across a hue
 * band around it (shrinking with depth). Even spacing — rather than a random
 * per-uid drift — guarantees siblings stay distinct (two users land on opposite
 * edges of their org's band instead of colliding). The optional
 * input/output/cached leaves shade their user's hue.
 */
export function buildSunburst(
  rows: UsageCostRow[],
  path: SunburstPath,
  metric: Metric
): SunburstData {
  const dimensions = path.dimensions;
  const withTokenLeaf = Boolean(path.tokenTypeLeaf) && metric !== 'calls';

  const nodes = new Map<string, SunburstNode>();

  const ensureNode = (
    id: string,
    label: string,
    parent: string,
    depth: number,
    seed: string,
    tokenType?: TokenType
  ): SunburstNode => {
    let node = nodes.get(id);
    if (!node) {
      node = { id, label, parent, value: 0, depth, seed, tokenType, hue: 0, color: '' };
      nodes.set(id, node);
    }
    return node;
  };

  // Pass 1: accumulate the tree and subtree totals (no colour yet).
  for (const row of rows) {
    const total = rowMetric(row, metric);
    if (total <= 0) continue;

    let parentId = '';
    let pathKey = '';

    for (const [depth, dim] of dimensions.entries()) {
      const label = dimensionLabel(row, dim);
      pathKey = pathKey ? `${pathKey}${ID_SEP}${label}` : label;
      ensureNode(pathKey, label, parentId, depth, dimensionSeed(row, dim)).value += total;
      parentId = pathKey;
    }

    if (withTokenLeaf) {
      for (const tokenType of TOKEN_TYPES) {
        const value = rowMetricByTokenType(row, metric, tokenType);
        if (value <= 0) continue;
        const id = `${pathKey}${ID_SEP}${tokenType}`;
        ensureNode(
          id,
          TOKEN_TYPE_LABEL[tokenType],
          parentId,
          dimensions.length,
          '',
          tokenType
        ).value += value;
      }
    }
  }

  // Index children by parent so we can spread siblings evenly.
  const childrenByParent = new Map<string, SunburstNode[]>();
  for (const node of nodes.values()) {
    const siblings = childrenByParent.get(node.parent);
    if (siblings) siblings.push(node);
    else childrenByParent.set(node.parent, [node]);
  }

  // Pass 2: assign hues/colours top-down.
  const assignChildren = (node: SunburstNode): void => {
    const children = childrenByParent.get(node.id) ?? [];
    const tokenLeaves = children.filter(
      (child): child is SunburstNode & { tokenType: TokenType } => child.tokenType !== undefined
    );
    const dimChildren = children
      .filter(child => child.tokenType === undefined)
      .sort((a, b) => b.value - a.value || (a.id < b.id ? -1 : 1));

    const spread = childHueSpread(node.depth + 1);
    for (const [index, child] of dimChildren.entries()) {
      child.hue = spreadHue(node.hue, spread, index, dimChildren.length);
      child.color = colorFromHue(child.hue, { lightness: lightnessForDepth(child.depth) });
      assignChildren(child);
    }

    for (const leaf of tokenLeaves) {
      leaf.hue = node.hue;
      leaf.color = shadeHueForTokenType(node.hue, leaf.tokenType);
    }
  };

  for (const root of childrenByParent.get('') ?? []) {
    root.hue = seededHue(root.seed);
    root.color = colorFromHue(root.hue, { lightness: lightnessForDepth(root.depth) });
    assignChildren(root);
  }

  const data: SunburstData = { ids: [], labels: [], parents: [], values: [], colors: [] };
  for (const node of nodes.values()) {
    data.ids.push(node.id);
    data.labels.push(node.label);
    data.parents.push(node.parent);
    data.values.push(node.value);
    data.colors.push(node.color);
  }
  return data;
}
